import { motion } from "framer-motion";
import { IMAGES } from "@/data/services";

const CHAPTERS = [
  {
    num: "01",
    title: "Precision",
    body: "Every transfer timed to the minute, every table held, every door already open. Your only task is to arrive — we choreograph everything after the wheels touch the tarmac.",
    image: IMAGES.dining,
    alt: "Candlelit fine dining room",
  },
  {
    num: "02",
    title: "Sanctuary",
    body: "Glacier caves lit for two, geothermal lagoons emptied for your party alone, villas where the northern lights perform on schedule. Privacy is the true luxury.",
    image: IMAGES.iceCaveAlt,
    alt: "Blue ice cave interior",
  },
  {
    num: "03",
    title: "Transcendence",
    body: "A gala inside a lava chamber. A DJ set on a glacier. Fireworks over a fjord at midnight. We stage the evenings your guests will describe for the rest of their lives.",
    image: IMAGES.aurora,
    alt: "Northern lights over mountains",
  },
];

export const Manifesto = () => (
  <section data-testid="manifesto-section" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-10 sm:py-36">
    <motion.p
      className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-300/80"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
    >
      The House Philosophy
    </motion.p>
    <motion.h2
      className="mt-4 max-w-2xl font-luxe text-2xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50 sm:text-4xl"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1 }}
    >
      Three vows, written in <span className="font-serif normal-case italic text-amber-200">gold</span>.
    </motion.h2>

    <div className="mt-20 space-y-24 sm:space-y-32">
      {CHAPTERS.map((ch, i) => (
        <motion.div
          key={ch.num}
          data-testid={`manifesto-chapter-${ch.num}`}
          className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-20 ${i % 2 === 1 ? "" : ""}`}
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className={i % 2 === 1 ? "lg:order-2" : ""}>
            <span className="font-serif text-6xl font-light italic text-amber-500/40 sm:text-7xl">{ch.num}</span>
            <h3 className="mt-2 font-luxe text-2xl uppercase tracking-[0.15em] text-neutral-50 sm:text-3xl">{ch.title}</h3>
            <div className="gold-hairline mt-6 w-24" />
            <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-400 sm:text-lg">{ch.body}</p>
          </div>
          <div className={`${i % 2 === 1 ? "lg:order-1" : ""} group relative`}>
            <div className="clipped-frame overflow-hidden border border-amber-500/20">
              <img
                src={ch.image}
                alt={ch.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-3 -right-3 -z-10 h-full w-full border border-amber-500/10" />
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);
